/**
 * Custom element `<mjl-my-apps>`. Style: `src/styles/` (ui-kit + Tailwind scan + custom.scss).
 */
import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styleMap } from 'lit/directives/style-map.js';
import { attachUiKitShadow, bus, getConfig, openApp, type MjlApp } from '@mjl/shared';
import { fetchFavoriteApps } from './api';
import { widgetCss } from './styles/css';

const MY_APPS_TAG = 'mjl-my-apps';
const GAP = 8;
const HUB_URL = import.meta.env.VITE_HUB_URL ?? '';

@customElement(MY_APPS_TAG)
export class MjlMyApps extends LitElement {
  @property({ type: Boolean, reflect: true }) open = false;
  @state() private triggerRect?: DOMRect;
  @state() private apps: MjlApp[] = [];
  @state() private loadError: string | null = null;
  @state() private loading = false;

  private async loadMyApps() {
    const cfg = getConfig();
    if (cfg?.myApps?.version === 2) {
      this.loading = false;
      this.apps = cfg.apps ?? [];
      this.loadError = null;
      return;
    }

    this.loading = true;
    this.loadError = null;
    try {
      const { data, error } = await fetchFavoriteApps();
      this.apps = data?.items ?? [];
      this.loadError = error;
    } finally {
      this.loading = false;
    }
  }

  connectedCallback() {
    super.connectedCallback();
    // CSS produk di-inline ke JS; Shadow Root agar tidak bocor ke halaman host.
    void attachUiKitShadow(this, widgetCss);
    this.unsubOpen = bus.on('my-apps:open', (payload) => {
      this.triggerRect = payload?.triggerRect;
      this.open = true;
      void this.loadMyApps();
    });
    this.unsubClose = bus.on('my-apps:close', () => {
      this.open = false;
    });
    this._boundOutsideClick = this.handleOutsideClick.bind(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.unsubOpen?.();
    this.unsubClose?.();
    document.removeEventListener('pointerdown', this._boundOutsideClick);
  }

  private unsubOpen?: () => void;
  private unsubClose?: () => void;
  private _boundOutsideClick: (e: PointerEvent) => void = () => {};

  updated(changedProps: Map<string, unknown>) {
    if (changedProps.has('open')) {
      if (this.open) {
        setTimeout(() => {
          document.addEventListener('pointerdown', this._boundOutsideClick);
        }, 0);
      } else {
        document.removeEventListener('pointerdown', this._boundOutsideClick);
      }
    }
  }

  private handleAppClick(app: MjlApp) {
    openApp(app);
    this.open = false;
    bus.emit('my-apps:close');
  }

  private openHub() {
    if (HUB_URL) window.open(HUB_URL, '_blank', 'noopener,noreferrer');
  }

  private handleOutsideClick(e: PointerEvent) {
    const path = e.composedPath();
    if (
      path.some((n) => n instanceof Element && n.closest('[data-mjl-trigger="my-apps"]'))
    ) {
      return;
    }
    if (path.includes(this)) return;
    this.open = false;
    bus.emit('my-apps:close');
  }

  /** Nilai runtime untuk `custom.scss` — bukan kumpulan CSS di atribut style. */
  private panelVars(): Record<string, string> {
    const rect = this.triggerRect;
    const mid = window.innerWidth / 2;

    if (!rect) {
      return {
        '--mjl-panel-top': '60px',
        '--mjl-panel-left': 'auto',
        '--mjl-panel-right': '16px',
      };
    }

    const top = `${rect.bottom + GAP}px`;
    const buttonCenter = rect.left + rect.width / 2;

    if (buttonCenter < mid) {
      return {
        '--mjl-panel-top': top,
        '--mjl-panel-left': `${rect.left}px`,
        '--mjl-panel-right': 'auto',
      };
    }

    return {
      '--mjl-panel-top': top,
      '--mjl-panel-left': 'auto',
      '--mjl-panel-right': `${window.innerWidth - rect.right}px`,
    };
  }

  private renderBody() {
    if (this.loading) {
      return html`
        <div class="flex flex-1 flex-col items-center justify-center">
          <div
            class="size-6 animate-spin rounded-full border-2 border-grey-200 border-t-grey-600"
            role="status"
            aria-label="Memuat"
          ></div>
        </div>
      `;
    }

    if (this.loadError) {
      return html`
        <div class="flex flex-1 flex-col items-center justify-center gap-3 px-6">
          <p class="text-center text-xs text-red-600">${this.loadError}</p>
          <button
            type="button"
            class="cursor-pointer rounded-lg border border-grey-200 px-3 py-1.5 text-sm font-medium text-grey-800 shadow-xs"
            @click=${() => this.loadMyApps()}
          >
            Coba lagi
          </button>
        </div>
      `;
    }

    if (this.apps.length === 0) {
      return html`
        <div class="flex flex-1 flex-col items-center justify-center gap-3 px-6">
          <img
            class="size-16"
            src="https://mustika.s3.ap-southeast-1.amazonaws.com/assets-public/icons/empty-data.svg"
            alt=""
          />
          <div class="text-center">
            <p class="font-medium text-grey-800">Belum ada aplikasi Favorit</p>
            <p class="mt-0.5 text-xs leading-4 text-grey-600">
              Tandai aplikasi yang sering digunakan dengan ikon bintang untuk ditampilkan disini
            </p>
          </div>
        </div>
      `;
    }

    return html`
      <div class="grid grid-cols-3 items-start gap-x-6 gap-y-3">
        ${this.apps.map(
          (app) => html`
            <button
              type="button"
              class="flex cursor-pointer flex-col items-center rounded-lg border-0 bg-transparent p-1 text-center hover:bg-grey-100"
              @click=${() => this.handleAppClick(app)}
            >
              <div class="flex size-12 shrink-0 items-center justify-center">
                ${app.logo_url
                  ? html`<img
                      class="size-12 object-contain"
                      src=${app.logo_url}
                      alt=${app.app_name}
                    />`
                  : html`<div
                      class="flex size-12 items-center justify-center rounded-lg bg-grey-100 text-xs font-bold text-grey-600"
                    >
                      ${app.app_initial_name}
                    </div>`}
              </div>
              <span class="w-full truncate text-center text-xs leading-4 text-grey-800"
                >${app.app_initial_name}</span
              >
            </button>
          `,
        )}
      </div>
    `;
  }

  render() {
    if (!this.open) return nothing;

    const showCount = !this.loading && !this.loadError;

    return html`
      <div
        class="mjl-my-apps-panel flex w-80 min-h-[276px] flex-col gap-4 rounded-xl border border-grey-200 bg-white p-4 shadow-dialog"
        role="dialog"
        aria-label="My Apps"
        style=${styleMap(this.panelVars())}
      >
        <div class="flex w-full items-center justify-between">
          <span class="text-sm font-semibold text-grey-800">Aplikasi Favorit</span>
          ${showCount
            ? html`<span class="text-sm text-grey-500">${this.apps.length}/5</span>`
            : nothing}
        </div>
        ${this.renderBody()}
        ${HUB_URL
          ? html`
              <button
                type="button"
                class="mjl-my-apps-explore mt-auto cursor-pointer rounded-lg border border-grey-200 py-1.5 shadow-xs"
                @click=${this.openHub}
              >
                <span class="text-sm font-medium text-grey-800">Jelajahi Aplikasi</span>
              </button>
            `
          : nothing}
      </div>
    `;
  }
}

export function registerMyApps(): void {
  if (!customElements.get(MY_APPS_TAG)) {
    customElements.define(MY_APPS_TAG, MjlMyApps);
  }
}

export { MY_APPS_TAG };
